import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/bottomSheet.css';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  showHandle?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  title,
  showHandle = true,
  style,
  className,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number>(0);
  const currentYRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Prevent body scroll when bottom sheet is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!sheetRef.current) return;
    
    // Only allow dragging from the handle area or top portion
    const target = e.target as HTMLElement;
    const isHandle = target.closest('.bottom-sheet-handle-area');
    const isHeader = target.closest('.bottom-sheet-header');
    
    if (!isHandle && !isHeader) return;

    isDraggingRef.current = true;
    startYRef.current = e.touches[0].clientY;
    currentYRef.current = 0;
    sheetRef.current.style.transition = 'none';
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDraggingRef.current || !sheetRef.current) return;

    const deltaY = e.touches[0].clientY - startYRef.current;
    
    // Only allow downward dragging
    if (deltaY > 0) {
      currentYRef.current = deltaY;
      sheetRef.current.style.transform = `translateY(${deltaY}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (!isDraggingRef.current || !sheetRef.current) return;

    isDraggingRef.current = false;
    sheetRef.current.style.transition = '';

    // If dragged down more than 100px, close the sheet
    if (currentYRef.current > 100) {
      onClose();
    } else {
      // Snap back to original position
      sheetRef.current.style.transform = '';
    }

    currentYRef.current = 0;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen && !isAnimating) return null;

  return createPortal(
    <div 
      className={`bottom-sheet-overlay ${isOpen ? 'open' : 'closing'}`}
      onClick={handleOverlayClick}
      onAnimationEnd={() => {
        if (!isOpen) setIsAnimating(false);
      }}
    >
      <div
        ref={sheetRef}
        className={`bottom-sheet ${isOpen ? 'open' : 'closing'} ${className || ''}`}
        style={style}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {showHandle && (
          <div className="bottom-sheet-handle-area">
            <div className="bottom-sheet-handle" />
          </div>
        )}
        
        <ModalCloseButton onClick={onClose} style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 1 }} />
        
        {title && (
          <div className="bottom-sheet-header">
            <h2 className="bottom-sheet-title">{title}</h2>
          </div>
        )}
        
        <div className="bottom-sheet-content">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BottomSheet;
